import React, { useCallback, useContext, useEffect, useState, useRef, useMemo } from 'react';
import {
  Typography, Box, Button, Dialog, DialogContent, DialogTitle,
} from '@material-ui/core';
import { useAptorApi, useFetchSelectOptions } from '../../../../../Api';
import { UserContext } from '../../../../../Context/UserContext/UserContext';
import { useIntl } from 'react-intl';
import { IProps, StepData, EditMode } from './types';
import EditStep1Choice from './EditStep1Choice';
import AddStep1CompanyUnit from './AddStep1CompanyUnit';
import AddStep2Responsible from './AddStep2Responsible';
import AddStep4LawsTable from './AddStep4LawsTable';
import AddStep5Confirm from './AddStep5Confirm';
import DeleteStep1CompanyUnit from './DeleteStep1CompanyUnit';
import DeleteStep2LawsTable from './DeleteStep2LawsTable';
import DeleteStep3Confirm from './DeleteStep3Confirm';
import { useSnackbar } from 'notistack';
import { EvaluationInfoEnum } from '../../../../../Context/EvaluationInfoEmun';

const PRIMARY = '#c4122f';

const STEP_CONFIG = {
  add: {
    withoutSkip: [
      { id: 'company-unit', component: AddStep1CompanyUnit, label: 'Company Unit' },
      { id: 'responsible', component: AddStep2Responsible, label: 'Responsible' },
      { id: 'laws', component: AddStep4LawsTable, label: 'Select Laws' },
      { id: 'confirm', component: AddStep5Confirm, label: 'Confirm' },
    ],
    withSkip: [
      { id: 'responsible', component: AddStep2Responsible, label: 'Responsible' },
      { id: 'laws', component: AddStep4LawsTable, label: 'Select Laws' },
      { id: 'confirm', component: AddStep5Confirm, label: 'Confirm' },
    ],
  },
  delete: {
    withoutSkip: [
      { id: 'company-unit', component: DeleteStep1CompanyUnit, label: 'Company Unit' },
      { id: 'laws', component: DeleteStep2LawsTable, label: 'Select Laws' },
      { id: 'confirm', component: DeleteStep3Confirm, label: 'Confirm' },
    ],
    withSkip: [
      { id: 'laws', component: DeleteStep2LawsTable, label: 'Select Laws' },
      { id: 'confirm', component: DeleteStep3Confirm, label: 'Confirm' },
    ],
  },
} as const;

export const EditLegalListWizard = ({
  open,
  onClose,
  onComplete,
}: IProps) => {
  const { companyUnits } = useContext(UserContext);
  const { api, abortController } = useAptorApi();
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const [editMode, setEditMode] = useState<EditMode>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedCompanyUnit, setSelectedCompanyUnit] = useState<any>(null);
  const [selectedLaws, setSelectedLaws] = useState<number[]>([]);
  const [selectedLawsData, setSelectedLawsData] = useState<any[]>([]);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [selectedRowsData, setSelectedRowsData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [stepData, setStepData] = useState<StepData>({});
  const [ourLawInfo, setOurLawInfo] = useState<any>(null);
  const [isCompanyUnitSkipped, setIsCompanyUnitSkipped] = useState(false);

  const DEFAULT_STEP_INFO = useMemo(() => ({
    'choise': {
      ourLawInformationSv: '',
      ourLawInformationEn: '',
    },
    'company-unit': {
      ourLawInformationSv: '',
      ourLawInformationEn: '',
    },
    'responsible': {
      ourLawInformationSv: '',
      ourLawInformationEn: '',
    },
  }), []);

  const sessionHydrated = useRef(false);

  const [users] = useFetchSelectOptions(
    useCallback(() => api.getCustomerUsers(), [api]),
    abortController,
  );

  useEffect(() => {
    if (open && !sessionHydrated.current) {
      setCurrentStep(1);
      setEditMode(null);
      setIsCompanyUnitSkipped(false);
      sessionHydrated.current = true;
    }
  }, [open]);

  const fetchOurLawStepInfo = React.useCallback(() => {
    api
      .getLawportalStepSettings(EvaluationInfoEnum.ourLawInfo)
      .then((result) => {
        if (abortController.current.signal.aborted) return;
        if (result) {
          try {
            const parsedResult = typeof result === 'string' ? JSON.parse(result) : result;
            setOurLawInfo(parsedResult);
          } catch (error) {
            console.error('Error parsing evaluation step info:', error);
            setOurLawInfo(DEFAULT_STEP_INFO);
          }
        } else {
          // Set default data if API returns null
          setOurLawInfo(DEFAULT_STEP_INFO);
        }
      })
      .catch((err) => {
        console.error('API error fetching step settings:', err);
        // Set default data on error
        setOurLawInfo(DEFAULT_STEP_INFO);
      });
  }, [api, abortController, DEFAULT_STEP_INFO]);

  const handleOurLawnStepInfoUpdate = React.useCallback(() => {
    fetchOurLawStepInfo();
  }, [fetchOurLawStepInfo]);

  useEffect(() => {
    fetchOurLawStepInfo();
  }, [fetchOurLawStepInfo]);

  const handleModeSelect = (mode: EditMode) => {
    // Clear step data if mode is changing to a different value
    if (mode !== editMode && editMode !== null) {
      setStepData({});
      setSelectedLaws([]);
      setSelectedLawsData([]);
      setSelectedRows([]);
      setSelectedRowsData([]);
    }
    setEditMode(mode);

    if (companyUnits && companyUnits.length === 1) {
      // Auto-select company unit and skip that step
      const unit = companyUnits[0];
      setSelectedCompanyUnit([unit]);
      setStepData(prev => ({
        ...prev,
        CustomerCompanyUnitId: unit.id,
        _companyUnitName: unit.name
      }));
      setIsCompanyUnitSkipped(true);
      setCurrentStep(2); 
    } else {
      setIsCompanyUnitSkipped(false);
      setCurrentStep(2);
    }
  };

  const handleStepNext = useCallback((data: Partial<StepData>) => {
    setStepData(prev => ({ ...prev, ...data }));
    if (data.selectedLawsData) {
      setSelectedLawsData(data.selectedLawsData);
    }
    setCurrentStep(prev => prev + 1);
  }, []);

  const handleStepBack = useCallback(() => {
    if (currentStep === 2) {
      // setEditMode(null);
      setIsCompanyUnitSkipped(false);
      setCurrentStep(1);
    } else {
      setCurrentStep(prev => Math.max(2, prev - 1));
    }
  }, [currentStep]);

  const handleClose = () => {
    setCurrentStep(1);
    setEditMode(null);
    setSelectedCompanyUnit(null);
    setSelectedLaws([]);
    setSelectedLawsData([]);
    setSelectedRows([]);
    setSelectedRowsData([]);
    setStepData({});
    setIsCompanyUnitSkipped(false);
    sessionHydrated.current = false;
    onClose();
  };

  const handleConfirmAdd = async () => {
    try {
      setIsLoading(true);

      const formData: any = {
        units: stepData.selectedUnits?.map((u: any) => u.id) || [stepData.CustomerCompanyUnitId] || [],
        complianceUsers: stepData.complianceUsers?.map((u: any) => u.value) || [],
        laws: selectedLaws,
      };

      let ok = await api.addCustomerLaws(formData);

      if (!ok && !abortController.current.signal.aborted) {
        let msg: string = formatMessage(
          { id: 'law-portal.manage.add-laws.warning' },
          {
            warning: formatMessage(
              { id: 'organization.user-unit-mismatch' },
              { alias: 'Company Unit' },
            ),
          },
        );
        enqueueSnackbar(msg, { variant: 'warning' });
      } else {
        enqueueSnackbar(
          formatMessage({ id: 'law-portal.manage.add-laws.success', defaultMessage: 'Laws added successfully!' }),
          { variant: 'success' }
        );
        handleClose();
        onComplete?.();
      }
    } catch (error) {
      enqueueSnackbar(
        formatMessage({ id: 'law-portal.manage.add-laws.error', defaultMessage: 'Failed to add laws' }),
        { variant: 'error' }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setIsLoading(true);
      if (selectedRows?.length > 0) {
        await api.removeMultipleCustomerLaw(selectedRows);
      }
      handleClose();
      onComplete?.();
    } catch (error) {
      setIsLoading(false);
    }
  };

  const shouldSkipCompanyUnit = companyUnits && companyUnits.length === 1;

  const getStepConfig = useCallback(() => {
    if (!editMode) return null;
    const skipMode = shouldSkipCompanyUnit ? 'withSkip' : 'withoutSkip';
    return STEP_CONFIG[editMode][skipMode];
  }, [editMode, shouldSkipCompanyUnit]);

  const stepConfig = useMemo(() => getStepConfig(), [getStepConfig]);

  const getTotalSteps = useCallback(() => {
    if (!editMode) return 0;
    return stepConfig?.length || 0;
  }, [editMode, stepConfig]);

  const getDisplayedStep = useCallback(() => {
    if (currentStep === 1) return 0;
    return currentStep - 1;
  }, [currentStep]);

  const getCurrentStepInfo = useCallback(() => {
    if (currentStep === 1 || !stepConfig) return null;
    const stepIndex = currentStep - 2;
    return stepConfig[stepIndex] || null;
  }, [currentStep, stepConfig]);

  const currentStepInfo = useMemo(() => getCurrentStepInfo(), [getCurrentStepInfo]);

  const renderStep = () => {
    // Step 1: Mode Selection
    if (currentStep === 1) {
      return (
        <EditStep1Choice
          onModeSelect={handleModeSelect}
          onCancel={handleClose}
          currentEditMode={editMode}
          currentStepName={"choise"}
          ourLawInfo={ourLawInfo}
          setOurLawInfo={handleOurLawnStepInfoUpdate}
        />
      );
    }

    // Render configured step dynamically
    if (!currentStepInfo) return null;

    const { component: StepComponent, id: stepId } = currentStepInfo;
    const commonProps = {
      currentData: stepData,
      isFirstStep: false,
      onCancel: handleClose,
      onNext: handleStepNext,
      onBack: handleStepBack,
    };

    // Render based on step ID
    switch (stepId) {
      case 'company-unit':
        return editMode === 'add' ? (
          <AddStep1CompanyUnit
            {...commonProps}
            companyUnits={companyUnits || []}
            onCompanyUnitSelect={setSelectedCompanyUnit}
            currentStepName={stepId}
            ourLawInfo={ourLawInfo}
            setOurLawInfo={setOurLawInfo}
          />
        ) : (
          <DeleteStep1CompanyUnit
            {...commonProps}
            companyUnits={companyUnits || []}
            onCompanyUnitSelect={setSelectedCompanyUnit}
            currentStepName={stepId}
            ourLawInfo={ourLawInfo}
            setOurLawInfo={handleOurLawnStepInfoUpdate}
          />
        );

      case 'responsible':
        return (
          <AddStep2Responsible
            {...commonProps}
            users={users}
            currentStepName={stepId}
            ourLawInfo={ourLawInfo}
            setOurLawInfo={handleOurLawnStepInfoUpdate}
          />
        );

      case 'laws':
        return editMode === 'add' ? (
          <AddStep4LawsTable
            {...commonProps}
            selectedLaws={selectedLaws}
            setSelectedLaws={setSelectedLaws}
            selectedLawsData={selectedLawsData}
            setSelectedLawsData={setSelectedLawsData}
          />
        ) : (
          <DeleteStep2LawsTable
            {...commonProps}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            selectedRowsData={selectedRowsData}
            setSelectedRowsData={setSelectedRowsData}
            selectedCompanyUnit={selectedCompanyUnit?.length?selectedCompanyUnit[0]:selectedCompanyUnit}
          />
        );

      case 'confirm':
        return editMode === 'add' ? (
          <AddStep5Confirm
            {...commonProps}
            selectedLawsData={selectedLawsData}
            selectedCompanyUnit={selectedCompanyUnit}
            selectedUsers={stepData.complianceUsers || []}
            hasResponsible={stepData.hasResponsible ?? false}
            onConfirmAdd={handleConfirmAdd}
          />
        ) : (
          <DeleteStep3Confirm
            {...commonProps}
            selectedRowsData={selectedRowsData}
            selectedCompanyUnit={selectedCompanyUnit?.length?selectedCompanyUnit[0]:selectedCompanyUnit}
            onConfirmDelete={handleConfirmDelete}
          />
        );

      default:
        return null;
    }
  };

  const totalSteps = useMemo(() => getTotalSteps(), [getTotalSteps]);
  const displayedStep = useMemo(() => getDisplayedStep(), [getDisplayedStep]);

  return (
    <>
      <Dialog open={open} maxWidth="lg" fullWidth disableEscapeKeyDown>
        <DialogTitle style={{ borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            {formatMessage({ id: 'law-portal.manage.legal-list.editLegalList', defaultMessage: 'Edit Legal List' })}
          </Typography>
        </DialogTitle>

        <DialogContent style={{ paddingTop: '24px', minHeight: '400px' }}>
          <Box style={{ minHeight: '420px' }}>
            {currentStep > 1 && (
              <Box style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Button variant="outlined" onClick={handleClose} disabled={isLoading} style={{ borderColor: PRIMARY, color: PRIMARY }}>
                    {formatMessage({ id: 'form.cancel' })}
                  </Button>
                </Box>
                <Button variant="outlined" onClick={handleStepBack} disabled={isLoading} style={{ borderColor: PRIMARY, color: PRIMARY }}>
                  {formatMessage({ id: 'component.back-button.back' })}
                </Button>
              </Box>
            )}

            {currentStep > 1 && editMode && (
              <Box
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 16px',
                  marginBottom: '12px',
                  backgroundColor: '#fdf2f3',
                  borderRadius: '6px',
                  borderLeft: `4px solid ${PRIMARY}`,
                  flexWrap: 'wrap',
                  gap: '8px',
                }}
              >
                <Typography variant="body2" style={{ fontWeight: 700, color: PRIMARY, fontSize: '13px' }}>
                  {formatMessage({ id: "law-portal.evalution.plan.step" }, { currentStep: displayedStep, totalSteps })}
                </Typography>

                <Box style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  {Array(totalSteps).fill(null).map((_, idx) => (
                    <Box
                      key={idx}
                      style={{
                        width: idx === displayedStep - 1 ? '20px' : '8px',
                        height: '8px',
                        borderRadius: '4px',
                        backgroundColor: idx < displayedStep ? PRIMARY : '#e0e0e0',
                        transition: 'all 0.3s ease',
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {renderStep()}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EditLegalListWizard;
